import React, { useEffect, useState } from 'react';
import Distance from 'components/Distance';
import CharacterStockIcon from 'react/chat/inner/display_pieces/messages/CharacterStockIcon';
import useCompactChatStyle from 'react/chat/inner/display_pieces/messages/kinds/useCompactChatStyle';
import { stopMyActiveSearch, getSearchListManager } from 'react/matchmaking/unified/header/myActiveSearch';
import { useMySearchClock } from 'react/matchmaking/unified/header/searchClock';
import { __ } from 'i18n/index';
import { MODE_NAMES } from 'react/chat/inner/display_pieces/messages/kinds/modeNames';

const CLOSED_LABELS = {
	taken: __('Taken'),
	cancelled: __('Cancelled'),
	expired: __('Expired'),
};

// The viewer's own search clock, when this card is that search (the same
// clock as the header's Play status).
function useCardSearchClock(matchId, running) {
	const { searchId, clock } = useMySearchClock();
	if(!running || !matchId || searchId !== String(matchId)) {
		return null;
	}
	return clock;
}

const CHALLENGE_FEED_CHECK_INTERVAL_MS = 1000;

// While this tab's challenge is pending, watch the searches feed so the card
// offers Challenge again once the poster declines it.
function useChallengeFeedWatch(message, challengePending) {
	const [, setFeedChecks] = useState(0);
	useEffect(() => {
		if(!challengePending || typeof message.syncLfgChallengeWithSearchFeed !== 'function') {
			return undefined;
		}
		const checkFeed = () => {
			if(message.syncLfgChallengeWithSearchFeed(getSearchListManager())) {
				setFeedChecks((checks) => checks + 1);
			}
		};
		checkFeed();
		const interval = setInterval(checkFeed, CHALLENGE_FEED_CHECK_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [message, challengePending]);
}

// Geographic distance between the viewer and the poster: severity 1-5 with the
// same colors and labels as the Play page's distance filter.
function distanceBetween(poster) {
	const viewerLocation = typeof myUser !== 'undefined' && myUser ? myUser.location : null;
	if(!poster || !poster.location || !viewerLocation || typeof poster.location.getDistanceFromLocation !== 'function') {
		return null;
	}
	const distance = poster.location.getDistanceFromLocation(viewerLocation);
	if(!distance) {
		return null;
	}
	const severity = distance.getDistanceSeverity();
	return { severity, label: Distance.distanceDescriptions.get(severity), color: Distance.distanceColors[severity] };
}

function DistanceValue({ distance }) {
	if(!distance) {
		return <span className='chat-lfg-card__value'>—</span>;
	}
	return (
		<span className='chat-lfg-card__value'>
			<span className='chat-lfg-card__distance-dot' style={{ backgroundColor: distance.color }} aria-hidden='true' />
			{distance.label}
		</span>
	);
}

function CardAction({ message, lfg, isMine, posterName }) {
	const isOpen = message.isLfgOpen();
	const isPending = !message.id;
	const searchClock = useCardSearchClock(lfg.match_id, isMine && isOpen && !isPending);
	useChallengeFeedWatch(message, !isMine && isOpen && !!message.lfgChallengeSent);

	if(!isOpen) {
		return (
			<button type='button' className='chat-lfg-card__go' disabled>
				{CLOSED_LABELS[lfg.status] || __('Closed')}
			</button>
		);
	}
	if(isMine && isPending && message.sendFailed) {
		return <span className='chat-lfg-card__wait chat-lfg-card__wait--failed'>{__('Not posted')}</span>;
	}
	if(isMine) {
		return (
			<span className='chat-lfg-card__searching'>
				<span className='chat-lfg-card__wait'>{searchClock ? `${__('Searching…')} ${searchClock}` : __('Searching…')}</span>
				{!isPending &&
				<button type='button' className='chat-lfg-card__stop' onClick={() => stopMyActiveSearch()}>{__('Stop')}</button>
				}
			</span>
		);
	}
	if(message.lfgChallengeSent) {
		return <span className='chat-lfg-card__wait'>{__('Challenge sent, waiting for %s…', posterName)}</span>;
	}
	return (
		<button type='button' className='chat-lfg-card__go' onClick={() => message.challengeLfg()}>
			{__('Challenge')}
		</button>
	);
}

function leadText(isMine, isClosed, posterName) {
	if(isMine) {
		return isClosed ? __('You were looking for a match') : __('You are looking for a match');
	}
	return isClosed ? __('%s was looking for a match', posterName) : __('%s is looking for a match', posterName);
}

// An LFG card in a room (message kind 'lfg'). Status changes arrive as pushes
// that update the message in place, so this reads the message on each render.
export default function LfgCard({ message }) {
	const compact = useCompactChatStyle();
	const lfg = message.getLfg();
	if(!lfg) {
		return null;
	}
	const isMine = message.isMyMessage();
	const poster = message.player;
	const posterName = poster ? poster.username : '';
	const modeName = MODE_NAMES[lfg.mode] || MODE_NAMES.friendlies;
	const distance = isMine ? null : distanceBetween(poster);
	const isClosed = !message.isLfgOpen();
	const classes = ['chat-lfg-card'];
	if(compact) {
		classes.push('chat-lfg-card--compact');
	}
	if(isClosed) {
		classes.push('chat-lfg-card--closed');
	}
	const action = <CardAction message={message} lfg={lfg} isMine={isMine} posterName={posterName} />;
	const error = message.lfgChallengeError && !isClosed
		? <span className='chat-lfg-card__error' role='alert'>{message.lfgChallengeError}</span>
		: null;

	if(compact) {
		const parts = [__('wants %s', modeName), lfg.main ? lfg.main.name : __('Any main')];
		if(distance) {
			parts.push(distance.label);
		}
		return (
			<span className={classes.join(' ')} data-testid='lfg-card'>
				<span className='chat-lfg-card__line'>
					{parts.join(' · ')}
					{lfg.title ? ` · “${lfg.title}”` : ''}
				</span>
				{action}
				{error}
			</span>
		);
	}

	// Closed: one line, so start-and-stop searches don't stack full cards in the room.
	if(isClosed) {
		classes.push('chat-lfg-card--strip');
		return (
			<div className={classes.join(' ')} data-testid='lfg-card'>
				<span className='chat-lfg-card__mode'>{modeName}</span>
				<span className='chat-lfg-card__strip-text'>
					{leadText(isMine, isClosed, posterName)}
					{lfg.main &&
					<span className='chat-lfg-card__strip-detail'>
						{' · '}<CharacterStockIcon character={lfg.main} />{lfg.main.name}
					</span>
					}
					<span className='chat-lfg-card__strip-detail'>
						{' · '}{lfg.best_of ? __('Best of %s', lfg.best_of) : __('Endless')}
					</span>
					{lfg.title && ` · “${lfg.title}”`}
				</span>
				<span className='chat-lfg-card__status'>{CLOSED_LABELS[lfg.status] || __('Closed')}</span>
			</div>
		);
	}

	return (
		<div className={classes.join(' ')} data-testid='lfg-card'>
			<div className='chat-lfg-card__lead'>
				<span className='chat-lfg-card__mode'>{modeName}</span>
				<span>{leadText(isMine, isClosed, posterName)}</span>
			</div>
			{/* No Game box: cards are only posted in their game's room. */}
			<div className='chat-lfg-card__fields'>
				<div className='chat-lfg-card__field' data-testid='lfg-main'>
					<span className='chat-lfg-card__label'>{__('Main')}</span>
					<span className='chat-lfg-card__value'>
						{lfg.main && <CharacterStockIcon character={lfg.main} />}
						{lfg.main ? lfg.main.name : __('Any')}
					</span>
				</div>
				<div className='chat-lfg-card__field' data-testid='lfg-distance'>
					<span className='chat-lfg-card__label'>{__('Distance')}</span>
					<DistanceValue distance={distance} />
				</div>
				<div className='chat-lfg-card__field' data-testid='lfg-best-of'>
					<span className='chat-lfg-card__label'>{__('Best of')}</span>
					<span className='chat-lfg-card__value'>{lfg.best_of ? lfg.best_of : __('Endless')}</span>
				</div>
				{action}
			</div>
			{error}
			{lfg.title &&
			<div className='chat-lfg-card__quote'>“{lfg.title}”</div>
			}
		</div>
	);
}
